import React, { useEffect, useMemo, useRef } from 'react'
import { fromUnknownError } from '~/prix'
import Loader from '~/prix/react/components/loader'
import useGoogleMaps from '~/prix/react/hooks/googleMaps'
import { MapType } from '../map/mapContext'
import boundsToCenterZoom from './boundsToCenterZoom'
import { MapContext } from './mapContext'

export type MapGeneralProps = {
  tilt?: number
  heading?: number
  mapType?: MapType
  children?: React.ReactNode
  is3D?: boolean
  styleProps?: {
    [key: string]: string
  }
  onClick?: (event: google.maps.MapMouseEvent) => void
  onContextMenu?: (event: google.maps.MapMouseEvent) => void
  onMouseUp?: (event: google.maps.MapMouseEvent) => void
  onMouseDown?: (event: google.maps.MapMouseEvent) => void
  onMouseMove?: (event: google.maps.MapMouseEvent) => void
  onMouseOut?: (event: google.maps.MapMouseEvent) => void
  onMouseOver?: (event: google.maps.MapMouseEvent) => void
  onDrag?: (event: google.maps.MapMouseEvent) => void
  onDragEnd?: (event: google.maps.MapMouseEvent) => void
  onDragStart?: (event: google.maps.MapMouseEvent) => void
  onZoomChanged?: (newZoom: number | undefined) => void
  width?: string
  height?: string
  isImmediately?: boolean
}

export type MapProps = (
  | {
      bounds?: never
      center: google.maps.LatLngLiteral
      zoom: number
    }
  | {
      bounds: google.maps.LatLngBoundsLiteral
      center?: never
      zoom?: never
    }
) &
  MapGeneralProps

/**
 * Componente de mapa com várias propriedades configuráveis.
 *
 * @param {Object} props - Propriedades do componente.
 * @param {Object} [props.bounds] - Limites do mapa.
 * @param {Object} [props.center] - Coordenadas do centro do mapa.
 * @param {number} [props.zoom] - Nível de zoom do mapa.
 * @param {number} [props.tilt=0] - Inclinação do mapa. Default: `0`.
 * @param {number} [props.heading=0] - Direção do mapa. Default: `0`.
 * @param {string} [props.mapType='roadmap'] - Tipo de mapa. Default: `'roadmap'`.
 * @param {boolean} [props.is3D=false] - Habilita visualização 3D. Default: `false`.
 * @param {boolean} [props.isImmediately=false] - Habilita a atualização do centro e zoom imediatamente. Default: `false`
 * @param {React.ReactNode} [props.children] - Componentes filhos (camadas).
 * @param {function} [props.onClick] - Função chamada no evento de clique.
 * @param {function} [props.onContextMenu] - Função chamada no evento de clique com botão direito.
 * @param {function} [props.onMouseUp] - Função chamada no evento de soltar o clique.
 * @param {function} [props.onMouseDown] - Função chamada no evento de clicar.
 * @param {function} [props.onMouseMove] - Função chamada no evento de mover o mouse.
 * @param {function} [props.onMouseOut] - Função chamada no evento de sair do mapa.
 * @param {function} [props.onMouseOver] - Função chamada no evento de passar o mouse.
 * @param {function} [props.onDrag] - Função chamada no evento de arrastar.
 * @param {function} [props.onDragEnd] - Função chamada no evento de soltar o arraste.
 * @param {function} [props.onDragStart] - Função chamada no evento de iniciar o arraste.
 * @param {function} [props.onZoomChanged] - Função chamada no evento de mudança de estado do zoom.
 */
export default function GoogleMap({
  bounds,
  center,
  zoom,
  tilt = 0,
  heading = 0,
  mapType = 'roadmap',
  styleProps,
  is3D = false,
  isImmediately = false,
  children,
  onClick,
  onContextMenu,
  onMouseUp,
  onMouseDown,
  onMouseMove,
  onMouseOut,
  onMouseOver,
  onDrag,
  onDragEnd,
  onDragStart,
  onZoomChanged,
  height = '100%',
  width = '100%',
}: MapProps) {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const { google, error, isLoading } = useGoogleMaps()
  const map = useMemo(() => {
    if (!google || !wrapperRef.current) {
      return null
    }

    const mapOptions: google.maps.MapOptions = {
      tilt,
      heading,
      zoom,
      center,
      mapId: is3D ? '3c724edc6ef4b699' : undefined,
      mapTypeId: mapType,
      disableDefaultUI: true,
      keyboardShortcuts: true,
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }],
        },
        {
          featureType: 'transit.station.bus',
          stylers: [{ visibility: 'off' }],
        },
        {
          featureType: 'transit.station.rail',
          stylers: [{ visibility: 'off' }],
        },
      ],
    }

    const map = new google.maps.Map(wrapperRef.current, mapOptions)
    ;(
      wrapperRef.current as HTMLDivElement & {
        mapInstance: google.maps.Map
      }
    ).mapInstance = map
    if (bounds) {
      map.fitBounds(bounds)
    }
    return map
  }, [google, wrapperRef.current, is3D])

  useEffect(() => {
    if (!map || !bounds || !google) {
      return
    }

    const currentBounds = map.getBounds()
    const targetBounds = new google.maps.LatLngBounds(bounds)

    if (isImmediately) {
      map.fitBounds(targetBounds)
      return
    }

    if (!currentBounds || isImmediately) {
      map.fitBounds(targetBounds)
      map.setTilt(tilt)
      map.setHeading(heading)
      return
    }

    const mapDimensions = {
      height: map.getDiv().clientHeight,
      width: map.getDiv().clientWidth,
    }

    const intermediateBounds = new google.maps.LatLngBounds(bounds).union(currentBounds)
    const intermediateCenterZoom = boundsToCenterZoom(intermediateBounds, mapDimensions)
    map.setZoom(intermediateCenterZoom.zoom)
    map.setCenter(intermediateCenterZoom.center)

    const timeout = setTimeout(() => {
      const targetCenterZoom = boundsToCenterZoom(targetBounds, mapDimensions)
      google.maps.event.addListenerOnce(map, 'idle', () => {
        if (zoom) {
          map.setZoom(zoom)
        }
        map.setTilt(tilt)
        map.setHeading(heading)
      })
      map.setCenter(targetCenterZoom.center)
      map.setZoom(targetCenterZoom.zoom)
    }, 1000)

    return () => {
      clearTimeout(timeout)
    }
  }, [
    map,
    google,
    bounds?.east,
    bounds?.north,
    bounds?.south,
    bounds?.west,
    zoom,
    tilt,
    heading,
    isImmediately,
  ])

  useEffect(() => {
    if (!map || !center || !zoom || !google) {
      return
    }

    const currentZoom = map.getZoom()
    const currentCenter = map.getCenter()

    if (!currentCenter || !currentZoom) {
      changeZoomAndPosition()
      return
    }

    if (isImmediately) {
      changeZoomAndPosition()
      return
    }

    const intermediateCenter = {
      lat: (center.lat + currentCenter.lat()) / 2,
      lng: (center.lng + currentCenter.lng()) / 2,
    }
    const intermediateZoom = Math.max(zoom, currentZoom)
    map.setZoom(intermediateZoom)
    map.setCenter(intermediateCenter)

    const timeout = setTimeout(() => {
      changeZoomAndPosition()
    }, 1000)

    return () => {
      clearTimeout(timeout)
    }
  }, [map, google, center?.lat, center?.lng, zoom, tilt, heading, isImmediately])

  const changeZoomAndPosition = () => {
    if (!map || !center || !zoom || !google) return

    map.setZoom(zoom)
    map.setCenter(center)
    map.setTilt(tilt)
    map.setHeading(heading)
  }

  const handleZoomChanged = () => {
    if (!map || !google) return

    if (onZoomChanged) {
      onZoomChanged(map?.getZoom())
    }
  }

  useEffect(() => {
    if (!map) {
      return
    }

    const listeners: google.maps.MapsEventListener[] = []
    if (onClick) {
      listeners.push(map.addListener('click', onClick))
    }
    if (onContextMenu) {
      listeners.push(map.addListener('contextmenu', onContextMenu))
    }
    if (onMouseUp) {
      listeners.push(map.addListener('mouseup', onMouseUp))
    }
    if (onMouseDown) {
      listeners.push(map.addListener('mousedown', onMouseDown))
    }
    if (onMouseMove) {
      listeners.push(map.addListener('mousemove', onMouseMove))
    }
    if (onMouseOut) {
      listeners.push(map.addListener('mouseout', onMouseOut))
    }
    if (onMouseOver) {
      listeners.push(map.addListener('mouseover', onMouseOver))
    }
    if (onDrag) {
      listeners.push(map.addListener('drag', onDrag))
    }
    if (onDragEnd) {
      listeners.push(map.addListener('dragend', onDragEnd))
    }
    if (onDragStart) {
      listeners.push(map.addListener('dragstart', onDragStart))
    }
    if (onZoomChanged) {
      listeners.push(map.addListener('zoom_changed', handleZoomChanged))
    }

    return () => {
      listeners.forEach(listener => listener.remove())
    }
  }, [
    map,
    onClick,
    onContextMenu,
    onMouseUp,
    onMouseDown,
    onMouseMove,
    onMouseOut,
    onMouseOver,
    onDrag,
    onDragEnd,
    onDragStart,
    onZoomChanged,
  ])

  useEffect(() => {
    if (!map) {
      return
    }

    map.setMapTypeId(mapType)
  }, [map, mapType, is3D])

  return (
    <MapContext.Provider value={{ map, google, mapType, is3D }}>
      <main style={{ height, width }}>
        {isLoading || !wrapperRef.current ? (
          <Loader />
        ) : error ? (
          <div>{fromUnknownError(error).message}</div>
        ) : !google || !map ? (
          <div>Google Maps indisponível</div>
        ) : null}
        <div
          className='google-map'
          tabIndex={-1}
          ref={wrapperRef}
          style={{ height, width, ...styleProps }}
        >
          {children}
        </div>
      </main>
    </MapContext.Provider>
  )
}
